import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { closeStop, getShipments } from '../../api/endpoints/admin';
import type { ShipmentResponse } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { useMutationHook } from '../../hooks/useMutationHook';
import { actionFailureMessage } from '../../api/actionFailure';
import { VehicleCell } from '../../components/domain/VehicleCell';
import { RouteText } from '../../components/domain/RouteText';
import { formatIst } from '../shipments/delayReason';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { stopPayerLabel, stopReasonLabel } from '../shipments/stopLabels';
import { STOP_INVALIDATE_KEYS } from '../shipments/stopQueries';
import styles from './StoppedTrucksView.module.css';

/**
 * Stopped trucks (dnm-work#165 C5): every shipment with an open stop, oldest stop first as the
 * server sends it. Replaces the Exceptions page; "Moving again" closes the stop in place and the
 * trip keeps its step (R53).
 */
export function StoppedTrucksView() {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stateRestore = location.state as { page?: number } | null;
  const initialPage = searchParams.get('page')
    ? Number(searchParams.get('page'))
    : (stateRestore?.page ?? 0);

  const [page, setPage] = useState(!Number.isNaN(initialPage) ? initialPage : 0);
  const [moving, setMoving] = useState<ShipmentResponse | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const buildReturnUrl = () => {
    const params = new URLSearchParams();
    if (page > 0) params.set('page', String(page));
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const navState = {
    from: buildReturnUrl(),
    fromLabel: t('stops.list.backLabel'),
    restoreState: { page },
  };

  const queryKey = ['admin', 'stopped', page] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => getShipments({ page, size: 25, stopped: true }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 2),
  });

  const movingMut = useMutationHook((s: ShipmentResponse) => closeStop(s.id, s.openStop!.id), {
    successMessage: t('stops.toast.closed'),
    invalidateKeys: STOP_INVALIDATE_KEYS,
    errorMessage: actionFailureMessage,
    onSuccess: () => setMoving(null),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <RefreshButton
          queryKeys={[
            ['admin', 'stopped'],
            ['admin', 'dashboard'],
          ]}
        />
      </div>

      <DataTable
        loading={isLoading}
        error={isError ? t('stops.list.loadError') : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={data?.items ?? []}
        empty={t('stops.list.empty')}
        columns={[
          {
            key: 'shipment',
            header: t('stops.list.colShipment'),
            render: (s) => (
              <>
                <Link to={`/shipments/${s.id}`} state={navState} className={styles.shipmentLink}>
                  <span className={styles.mono}>#{s.id}</span> <RouteText route={s} />
                </Link>
                {/* Vehicle sits under the shipment so the row fits at 1200 px @125%. */}
                <span className={styles.vehicle}>
                  <VehicleCell assignment={s.assignment} />
                </span>
              </>
            ),
          },
          {
            key: 'reason',
            header: t('stops.list.colReason'),
            render: (s) => (
              <>
                <span className={styles.reason}>{stopReasonLabel(t, s.openStop?.reason)}</span>
                {s.openStop?.note && (
                  <span className={styles.note} title={s.openStop.note}>
                    {s.openStop.note}
                  </span>
                )}
                {/* Responsible and step sit under the reason so the row fits at 1200 px @125%
                    and Moving again stays in view (UX 1/7). */}
                <span className={styles.meta}>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t('stops.list.colPayer')}</span>{' '}
                    <span className={s.openStop?.payer ? undefined : styles.notSet}>
                      {stopPayerLabel(t, s.openStop?.payer)}
                    </span>
                  </span>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t('stops.list.colStage')}</span>{' '}
                    <span title={s.status}>{shipmentStatusLabel(s.status)}</span>
                  </span>
                </span>
              </>
            ),
          },
          {
            key: 'since',
            header: t('stops.list.colSince'),
            width: '170px',
            render: (s) =>
              s.openStop ? (
                <>
                  <span className={styles.time}>{formatIst(s.openStop.startedAt)}</span>
                  <span className={styles.expected}>
                    {s.openStop.expectedResumeAt
                      ? t('stops.list.expectedLine', {
                          time: formatIst(s.openStop.expectedResumeAt),
                        })
                      : t('stops.list.noExpected')}
                  </span>
                </>
              ) : (
                '—'
              ),
          },
          {
            key: 'actions',
            header: '',
            width: '130px',
            render: (s) =>
              s.openStop ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setDialogError(null);
                    setMoving(s);
                  }}
                >
                  {t('stops.actions.movingAgain')}
                </Button>
              ) : null,
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      <ConfirmDialog
        isOpen={moving !== null}
        title={t('stops.moving.title')}
        description={
          moving
            ? t('stops.moving.description', {
                shipmentId: moving.id,
                stage: shipmentStatusLabel(moving.status),
              })
            : ''
        }
        confirmText={t('stops.moving.confirm')}
        variant="primary"
        help="moving-again"
        loading={movingMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          if (moving?.openStop) movingMut.mutate(moving);
        }}
        onCancel={() => {
          setDialogError(null);
          setMoving(null);
        }}
      />
    </div>
  );
}
